import { Mail, Phone, MapPin, Send, CheckCircle2, FileText, Rocket, Award } from 'lucide-react';
import { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card'
import { Input } from './ui/input'
import { Textarea } from './ui/textarea'
import { Label } from './ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select'

const contactInfo = [
  {
    icon: Mail,
    title: "E-post",
    value: "kontakt@pyksel.no",
    link: "mailto:kontakt@pyksel.no",
    description: "Send oss en melding"
  }
]

export function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '', // Added phone number field
    company: '',
    project: '',
    message: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isSent, setIsSent] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);

  useEffect(() => {
    if (isSent) {
      const timer = setTimeout(() => {
        setIsSent(false);
      }, 5000);

      return () => clearTimeout(timer);
    }
  }, [isSent]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { id, value } = e.target;
    setFormData((prevData) => ({ ...prevData, [id]: value }));
  };

  const handleSelectChange = (value: string) => {
    setFormData((prevData) => ({ ...prevData, project: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const response = await fetch('/send-email.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      const result = await response.json();
      console.log(result.message);

      setIsSent(true);
      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        project: '',
        message: '',
      });
    } catch (error) {
      console.error('Error sending email:', error);
      alert('Noe gikk galt, prøv igjen.');
    } finally {
      setIsLoading(false);
    }
  };

  const contactRef = useRef(null);
  const isInView = useInView(contactRef, { once: true, margin: "200px 0px 0px 0px" });

  const variants = {
    hidden: { opacity: 0, y: 50 },
    visible: { opacity: 1, y: 0 },
  };

  return (
    <section className="py-24 px-6 bg-white">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold mb-4">
            <span className="gradient-text">Få i gang</span> <span className="text-black">ditt prosjekt</span>
          </h2>
          <p className="text-xl text-gray-800 max-w-2xl mx-auto">
            Klar for å ta merkevaren din til neste nivå? La oss snakke om hvordan vi kan hjelpe deg.
          </p>
        </div>

        <motion.div
          ref={contactRef}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          variants={variants}
          transition={{ duration: 0.8 }}
          className="grid grid-cols-1 lg:grid-cols-3 gap-8"
        >
          {/* Contact Info */}
          <div className="space-y-6">
            {contactInfo.map((info, index) => (
              <motion.div
                key={index}
                variants={variants}
                transition={{ duration: 0.5, delay: 0.2 + index * 0.2 }}
              >
                <Card className="glass-card border-0 h-full">
                  <CardContent className="p-6">
                    <div className="flex items-center space-x-4">
                      <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                        <info.icon className="w-6 h-6 text-primary" />
                      </div>
                      <div>
                        <h3 className="font-semibold">{info.title}</h3>
                        {info.link ? (
                          <a href={info.link} className="text-primary hover:underline transition-all">
                            {info.value}
                          </a>
                        ) : (
                          <p className="text-primary">{info.value}</p>
                        )}
                        <p className="text-sm text-muted-foreground">{info.description}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
            <motion.div
              variants={variants}
              transition={{ duration: 0.5, delay: 0.4 }}
            >
              <Card className="glass-card border-0 h-full">
                <CardHeader>
                  <h3 className="text-xl font-semibold">Hva kan du forvente?</h3>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-4">
                    <li className="flex items-start space-x-4">
                      <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                        <FileText className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <h4 className="font-semibold">Første gjennomgang</h4>
                        <p className="text-sm text-muted-foreground">Vi ser over din henvendelse og forbereder et tilbud.</p>
                      </div>
                    </li>
                    <li className="flex items-start space-x-4">
                      <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                        <Rocket className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <h4 className="font-semibold">Prosjektforslag</h4>
                        <p className="text-sm text-muted-foreground">Du mottar et detaljert forslag med tidslinje og pris.</p>
                      </div>
                    </li>
                    <li className="flex items-start space-x-4">
                      <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                        <Award className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <h4 className="font-semibold">Prosjektstart</h4>
                        <p className="text-sm text-muted-foreground">Etter godkjenning starter vi prosjektet og realiserer dine ideer.</p>
                      </div>
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </motion.div>
          </div>

          {/* Contact Form */}
          <motion.div
            variants={variants}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="lg:col-span-2"
          >
            <Card className="glass-card border-0 relative">
              {isSent && (
                <div className="absolute inset-0 z-10 flex flex-col items-center justify-center p-8 bg-primary rounded-lg text-center">
                  <CheckCircle2 className="w-16 h-16 text-primary-foreground mb-4" />
                  <h3 className="text-2xl font-semibold text-primary-foreground mb-2">Takk! Meldingen er sendt.</h3>
                  <p className="text-primary-foreground">
                    En bekreftelse er sendt til din e-post. Vi tar kontakt med deg snart!
                  </p>
                </div>
              )}
              <CardHeader>
                <h3 className="text-2xl font-semibold">Send oss en melding</h3>
                <CardDescription>
                  Beskriv prosjektet ditt, så tar vi kontakt innen 24 timer
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form ref={formRef} onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                      <Label htmlFor="name">Navn</Label>
                      <Input id="name" name="name" placeholder="Ditt navn" value={formData.name} onChange={handleChange} required autoComplete="name" />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email">E-post</Label>
                      <Input id="email" name="email" type="email" placeholder="Din@epost.no" value={formData.email} onChange={handleChange} required autoComplete="email" />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="phone">Telefonnummer</Label>
                    <Input id="phone" name="phone" type="tel" placeholder="Ditt telefonnummer" value={formData.phone} onChange={handleChange} required autoComplete="tel" />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="company">Bedrift</Label>
                    <Input id="company" name="company" placeholder="Bedriftsnavn (valgfri)" value={formData.company} onChange={handleChange} autoComplete="organization" />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="project">Prosjekttype</Label>
                    <Select onValueChange={handleSelectChange} value={formData.project} name="project">
                      <SelectTrigger id="project" className={`w-full text-base md:text-sm ${formData.project ? 'text-white' : 'text-muted-foreground'}`} aria-label="Prosjekttype">
                        <SelectValue placeholder="Velg prosjekttype" />
                      </SelectTrigger>
                      <SelectContent className="dark">
                        <SelectItem value="brand-design">Brand Design</SelectItem>
                        <SelectItem value="web-design">Web Design</SelectItem>
                        <SelectItem value="app-design">App Design</SelectItem>
                        <SelectItem value="digital-marketing">Digital Marketing</SelectItem>
                        <SelectItem value="other">Annet</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message">Melding</Label>
                    <Textarea
                      id="message"
                      placeholder="Fortell oss om prosjektet ditt..."
                      className="text-base md:text-sm"
                      rows={6}
                      value={formData.message}
                      onChange={handleChange}
                      required
                      name="message"
                      autoComplete="off"
                    />
                  </div>

                  <Button
                    type="submit"
                    className="w-full group hero-glow smooth-transition hover:scale-105"
                    size="lg"
                    disabled={isLoading}
                  >
                    {isLoading ? 'Sender...' : 'Send melding'}
                    <Send className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </form>
              </CardContent>
            </Card>
          </motion.div>
        </motion.div>
      </div>
    </section>
  )
}
